import { AccountBasisSummary, BasisCell } from '@/features/cost-basis/components';
import { useBasisEnabled } from '@/features/cost-basis/context';
import { useMemo } from 'react';
import { AccountDetailsPanel, ALLOCATION_CLASSES } from '@wavvest/wealth-ui';
import type { AllocationValues } from '@wavvest/wealth-ui';
import { ApexAccount } from '@/types/wealth';
import { useTheme } from '@/contexts/ThemeContext';
import { useAppSelector } from '@/store/hooks';
import {
  selectApexAssetMap,
  selectApexLoading,
  selectHoldingsFailedById,
} from '@/store/features/apex/apex-selectors';
import {
  generateAssetClassesView,
  getUnclassifiedApexAllocation,
  normalizeApexAssetClass,
} from '@/util/planning-util';
import { formatTransferDate } from '@/util/firm-data-transfers';

interface AccountCollapseProps {
  account: ApexAccount;
}

const AccountCollapse: React.FC<AccountCollapseProps> = ({ account }) => {
  const basisEnabled = useBasisEnabled();
  const { theme } = useTheme();
  const assetMap = useAppSelector(selectApexAssetMap);
  const assetMapLoading = useAppSelector(selectApexLoading).assetMap;
  const holdingsFailed = Boolean(useAppSelector(selectHoldingsFailedById)[account.id]);

  const allocation = useMemo<AllocationValues | null>(() => {
    // Wait for the asset map only while it's loading; if it failed, classify
    // without it (unmapped holdings land in Other) instead of loading forever.
    if (account.holdings === undefined || (!assetMap && assetMapLoading)) {
      return null;
    }
    const values: AllocationValues = Object.fromEntries(
      ALLOCATION_CLASSES.map((item) => [item.name, 0]),
    );
    if (account.holdings.length === 0) {
      return values;
    }
    try {
      const treeData = generateAssetClassesView([account], account.id, assetMap);
      treeData?.children?.forEach((child) => {
        const name = normalizeApexAssetClass(child.text);
        if (name in values) {
          values[name] = child.valueUsd;
        }
      });
      // Holdings missing from the class_tree (e.g. the FDIC cash sweep) are
      // dropped by buildTree, so fold them in regardless of the tree result.
      const unclassified = getUnclassifiedApexAllocation([account], assetMap);
      Object.keys(values).forEach((name) => {
        values[name] += unclassified[name] ?? 0;
      });
      return values;
    } catch (error) {
      console.error('Failed to generate asset allocation data:', error);
      return {};
    }
  }, [account, assetMap, assetMapLoading]);

  return (
    <AccountDetailsPanel
      accountId={account.id}
      costBasisSummary={
        basisEnabled ? (
          <AccountBasisSummary accountId={account.id} name={account.name} />
        ) : undefined
      }
      renderCostBasis={
        basisEnabled
          ? (holding, gain) => <BasisCell accountId={account.id} holding={holding} gain={gain} />
          : undefined
      }
      totalEquity={account.total_equity}
      holdings={account.holdings}
      holdingsError={holdingsFailed}
      transfers={account.transferList}
      allocation={allocation}
      theme={theme}
      formatDate={formatTransferDate}
    />
  );
};

export default AccountCollapse;
